<script lang="ts">
  import fw from '../fernwartung.json';

  /* Der Client liest Server und Schlüssel aus seinem Dateinamen — deshalb
     der lange Name. Er muss exakt so beim Kunden ankommen; der Link zeigt
     direkt auf die Datei, der Browser übernimmt den Namen aus der Adresse.
     Das Schlusskomma fängt Windows' „ (1)" bei Doppel-Downloads ab — gleiche
     Formel wie in scripts/fernwartung-client.mjs. */
  const dateiname = `rustdesk-host=${fw.host},key=${fw.key},.exe`;
  /* Unkodiert: „=" und „," sind in Pfaden erlaubt, und so bekommt jeder
     Server (nginx, Vite-Vorschau) denselben Dateinamen zu sehen. */
  const downloadPfad = `/downloads/${dateiname}`;

  /* Mac und Linux lesen den Dateinamen nicht. Dort trägt ein Link der Form
     rustdesk://config/<Text> den Server ein — derselbe Text, den RustDesk
     unter „Server-Konfiguration exportieren" erzeugt: JSON mit host und key,
     base64url ohne Auffüllzeichen, dann Zeichen für Zeichen umgedreht
     (src/custom_server.rs, get_custom_server_from_config_string). */
  const konfigText = [...btoa(JSON.stringify({ host: fw.host, key: fw.key }))]
    .join('')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
    .split('')
    .reverse()
    .join('');
  const konfigLink = `rustdesk://config/${konfigText}`;

  let kopiert = $state<string | null>(null);

  async function kopiere(text: string, was: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      kopiert = was;
      setTimeout(() => (kopiert = null), 2000);
    } catch {
      /* Ohne Zwischenablage (alte Browser) bleibt der Text zum Abtippen sichtbar. */
    }
  }

  $effect(() => {
    const vorher = document.title;
    document.title = 'Fernwartung — IT-Service Karsten Fischer';
    return () => {
      document.title = vorher;
    };
  });
</script>

<section class="hero tb-gradient">
  <div class="breite">
    <div>
      <span class="hero-vorzeile">Fernwartung</span>
      <h1>Hilfe direkt auf Ihrem Bildschirm.<br /><span class="akzent">Ohne Anfahrt.</span></h1>
      <p class="hero-lead">
        Wir telefonieren, und ich sehe dabei, was Sie sehen. Sie behalten Maus und Tastatur und
        können die Verbindung jederzeit beenden.
      </p>

      <div class="hero-cta">
        <a class="tb-btn-primary gross" href={downloadPfad} download={dateiname}>
          ⬇ Fernwartung für Windows laden
        </a>
        <a class="tb-btn-sekundaer gross hell" href="tel:+4975683090137">📞 Erst anrufen</a>
      </div>
      <p class="hero-klein">
        Version {fw.version} · {fw.windows.groesseMb} MB · keine Installation nötig ·
        <a href="#andere-geraete">Mac, Linux, Handy oder Tablet?</a>
      </p>
    </div>
  </div>
</section>

<section class="abschnitt">
  <div class="breite">
    <div class="mitte">
      <h2>So geht's — in drei Schritten</h2>
      <p class="vorspann">
        Am besten machen wir das gemeinsam am Telefon. Sie brauchen nur die Datei von oben.
      </p>
    </div>

    <div class="karten-grid">
      <div class="karte">
        <div class="symbol">1</div>
        <h3>Datei öffnen</h3>
        <p class="leise">
          Klicken Sie oben auf den Knopf und öffnen Sie die heruntergeladene Datei. Es öffnet sich
          ein kleines Fenster — mehr passiert noch nicht.
        </p>
      </div>
      <div class="karte">
        <div class="symbol">2</div>
        <h3>ID und Passwort vorlesen</h3>
        <p class="leise">
          Im Fenster stehen eine <strong>ID</strong> (neun Ziffern) und ein
          <strong>Einmal-Passwort</strong>. Lesen Sie mir beides am Telefon vor. Das Passwort ist nur
          für diese eine Sitzung gültig.
        </p>
      </div>
      <div class="karte">
        <div class="symbol">3</div>
        <h3>Zusehen — und beenden</h3>
        <p class="leise">
          Sie sehen jeden Mausklick live mit. Zum Beenden schließen Sie einfach das Fenster. Danach
          komme ich nicht mehr auf Ihren Rechner — auch nicht, wenn die Datei liegen bleibt.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="abschnitt hell">
  <div class="breite">
    <div class="zwei-spalten">
      <div>
        <h2>Woran Sie erkennen, dass alles echt ist</h2>
        <p class="leise">
          Betrüger rufen gern als „Microsoft-Support" an und wollen auf Ihren Rechner. Deshalb gilt bei
          uns eine feste Regel: <strong>Eine Fernwartung beginnt nur, wenn Sie sie selbst gewollt
          haben und mit mir telefonieren.</strong>
        </p>
        <p class="leise">
          Ich rufe nie unangekündigt an, um „ein Problem auf Ihrem Computer" zu beheben. Bekommen
          Sie so einen Anruf: auflegen, und im Zweifel mich unter der bekannten Nummer zurückrufen.
        </p>
      </div>
      <div class="karte">
        <h3>Ihre Sicherheit</h3>
        <ul class="checkliste">
          <li>Nur mit Ihrem Einmal-Passwort — Sie entscheiden jedes Mal neu</li>
          <li>Sie sehen alles live mit und behalten die Kontrolle</li>
          <li>Fenster schließen beendet die Verbindung sofort</li>
          <li>Kein dauerhafter Zugriff, nichts bleibt im Hintergrund</li>
          <li>Verbindung verschlüsselt, Vermittlung über unseren Server in Deutschland</li>
        </ul>
      </div>
    </div>
  </div>
</section>

<section class="abschnitt" id="andere-geraete">
  <div class="breite">
    <div class="mitte">
      <h2>Mac und Linux</h2>
      <p class="vorspann">
        Zwei Schritte: das Programm vom Hersteller laden und installieren, dann mit einem Klick
        unseren Server eintragen. Am Telefon gehe ich das gern mit Ihnen durch.
      </p>
    </div>

    <div class="zwei-spalten">
      <div class="karte">
        <h3>1. RustDesk installieren</h3>
        <ul class="checkliste">
          <li>
            <span>
              Mac mit Apple-Chip (M1 bis M4):
              <a href={fw.andere.macApple} rel="noopener">Download (.dmg)</a>
            </span>
          </li>
          <li>
            <span>Mac mit Intel-Chip: <a href={fw.andere.macIntel} rel="noopener">Download (.dmg)</a></span>
          </li>
          <li>
            <span>
              Linux (Ubuntu, Debian, Mint):
              <a href={fw.andere.linuxDeb} rel="noopener">Download (.deb)</a>
            </span>
          </li>
          <li>
            <span>
              Andere Linux-Systeme:
              <a href={fw.andere.linuxAppImage} rel="noopener">Download (.AppImage)</a>
            </span>
          </li>
        </ul>
        <p class="leise klein">
          Die Links führen direkt zur Hersteller-Veröffentlichung (Version {fw.version}). Auf dem Mac
          die App aus dem geöffneten Fenster in „Programme" ziehen; beim ersten Start fragt macOS nach
          Bildschirmaufnahme und Bedienungshilfen — beides erlauben, sonst sehe ich nur ein schwarzes
          Bild.
        </p>
      </div>
      <div class="karte">
        <h3>2. Unseren Server eintragen</h3>
        <p class="leise">
          Ist RustDesk installiert und einmal geöffnet, genügt ein Klick. Der Browser fragt, ob er
          RustDesk öffnen darf — erlauben.
        </p>
        <p>
          <a class="tb-btn-sekundaer" href={konfigLink}>Server in RustDesk eintragen</a>
        </p>
        <p class="leise klein">
          Danach steht unten im RustDesk-Fenster „Bereit". Fertig — ab jetzt läuft es wie bei
          Windows: ID und Einmal-Passwort am Telefon vorlesen.
        </p>
      </div>
    </div>

    <div class="mitte handy">
      <h2>Android-Handy oder -Tablet</h2>
      <p class="vorspann">
        App „RustDesk Remote Desktop" aus Google Play laden und einmalig unseren Server von Hand
        eintragen. Danach stehen ID und Einmal-Passwort direkt auf der Startseite der App.
      </p>
    </div>

    <div class="zwei-spalten">
      <div class="karte">
        <h3>Wo eintragen</h3>
        <p class="leise">
          In der App: Einstellungen (Zahnrad) → <strong>ID/Relay-Server</strong>. Die beiden Werte
          rechts eintragen, Relay- und API-Server leer lassen, dann <strong>OK</strong>.
        </p>
        <p class="leise">
          Zum Start der Sitzung tippt man auf der Startseite auf <strong>Bildschirmaufnahme
          starten</strong> und erlaubt sie. Soll ich auch tippen können, verlangt Android zusätzlich
          die <strong>Eingabesteuerung</strong> über die Bedienungshilfen — die App führt dorthin.
        </p>
        <p class="leise klein">
          Die Handeingabe geht auch auf Mac und Linux, falls der Klick oben nicht funktioniert.
        </p>
      </div>
      <div class="karte">
        <h3>Die Werte</h3>
        <dl class="einstellungen">
          <dt>ID-Server</dt>
          <dd>
            <code>{fw.host}</code>
            <button type="button" class="kopieren" onclick={() => kopiere(fw.host, 'host')}>
              {kopiert === 'host' ? 'Kopiert ✓' : 'Kopieren'}
            </button>
          </dd>
          <dt>Key</dt>
          <dd>
            <code>{fw.key}</code>
            <button type="button" class="kopieren" onclick={() => kopiere(fw.key, 'key')}>
              {kopiert === 'key' ? 'Kopiert ✓' : 'Kopieren'}
            </button>
          </dd>
        </dl>
      </div>
    </div>

    <div class="karte iphone">
      <h3>iPhone und iPad</h3>
      <p class="leise">
        Apple erlaubt keine Fernsteuerung des iPhones durch Apps wie RustDesk — die RustDesk-App
        für iPhone kann nur selbst steuern, nicht gesteuert werden. Dort gibt es deshalb keine ID und
        kein Einmal-Passwort.
      </p>
      <p class="leise">
        Was stattdessen gut funktioniert: Wir telefonieren per <strong>FaceTime</strong>, und Sie
        teilen Ihren Bildschirm (im FaceTime-Anruf oben auf das Symbol mit dem Rechteck tippen →
        <strong>Meinen Bildschirm teilen</strong>). Ich sehe dann alles mit und sage Ihnen, wo Sie
        tippen.
      </p>
    </div>
  </div>
</section>

<section class="abschnitt hell">
  <div class="breite">
    <div class="mitte">
      <h2>Was dahintersteckt</h2>
    </div>
    <div class="karten-grid">
      <div class="karte">
        <h3>Eigener Server, in Deutschland</h3>
        <p class="leise">
          Kein TeamViewer, kein AnyDesk, kein Dienst in Übersee: Die Verbindung wird über unseren
          eigenen Server vermittelt. Die Bildschirminhalte sind Ende-zu-Ende verschlüsselt — auch der
          Server sieht sie nicht.
        </p>
      </div>
      <div class="karte">
        <h3>Offene Software</h3>
        <p class="leise">
          Wir nutzen <a href="https://github.com/rustdesk/rustdesk" rel="noopener">RustDesk</a>,
          freie Software unter der AGPL-Lizenz. Die Datei oben ist das unveränderte Original des
          Herstellers — nur der Dateiname trägt unsere Serveradresse.
        </p>
      </div>
      <div class="karte">
        <h3>Datenschutz</h3>
        <p class="leise">
          Fernwartung erfolgt nur mit Ihrer Zustimmung, zeitlich begrenzt und zweckgebunden. Details
          stehen in unserer <a href="/datenschutz">Datenschutzerklärung</a>, Abschnitt „Support- und
          Fernwartung".
        </p>
      </div>
    </div>
    <p class="leise klein hinweis">
      Windows meldet bei unbekannten Programmen manchmal „Der Computer wurde durch Windows geschützt".
      Das ist eine allgemeine Warnung für Software außerhalb des Microsoft Store. Prüfen Sie, dass
      Sie die Datei von <strong>it-karsten-fischer.de</strong> geladen haben — dann „Weitere Informationen"
      und „Trotzdem ausführen". Im Zweifel: anrufen, wir schauen gemeinsam.
    </p>
  </div>
</section>

<style>
  .tb-btn-sekundaer.hell {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.55);
  }

  .tb-btn-sekundaer.hell:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: #fff;
  }

  .hero-klein {
    margin-top: 1.25rem;
    color: var(--tb-light-100);
    font-size: 0.95rem;
  }

  .hero-klein a {
    color: #fff;
  }

  .klein {
    font-size: 0.95rem;
  }

  .hinweis {
    max-width: 46rem;
    margin: 2.5rem auto 0;
    text-align: center;
  }

  .handy {
    margin-top: 3.5rem;
  }

  .iphone {
    margin-top: 1.5rem;
  }

  .checkliste li span {
    display: inline;
  }

  .einstellungen {
    margin: 1rem 0;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.6rem 1rem;
    align-items: center;
  }

  .einstellungen dt {
    font-weight: 700;
    color: var(--tb-navy-900);
  }

  .einstellungen dd {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
    min-width: 0;
  }

  .einstellungen code {
    background: var(--tb-surface-50);
    border: 1px solid #e7edf3;
    border-radius: 8px;
    padding: 0.35em 0.6em;
    font-size: 0.92rem;
    word-break: break-all;
  }

  .kopieren {
    font: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--tb-navy-700);
    background: transparent;
    border: 2px solid var(--tb-navy-700);
    border-radius: var(--tb-radius-pill);
    padding: 0.25em 0.9em;
    cursor: pointer;
  }

  .kopieren:hover {
    background: var(--tb-surface-50);
  }

  @media (max-width: 40rem) {
    .einstellungen {
      grid-template-columns: 1fr;
      gap: 0.25rem 0;
    }

    .einstellungen dd {
      margin-bottom: 0.75rem;
    }
  }
</style>
